<template>
    <div
        v-if="state !== null"
        class="device-controls__block"
        :class="{
            'dci__order-2': generatorAction != null,
            'dci__order-1': generatorAction != null && generatorInstance === instance,
        }"
    >
        <!-- Block title -->
        <div class="device-controls__row device-controls__row--title">
            <span class="dci__title">
                <span class="sg-vrm-icon_112_generator-24px"></span>
                {{ $t('dashboard.generator') }} {{ showInstance ? instance : '' }}
            </span>
            <div
                v-if="
                    !isStopGeneratorConfirmation &&
                        !isResetServiceConfirmation &&
                        isWaitingForResponse
                "
                class="dci__status"
            >
                <spinner />
                {{ $t('dashboard.dci.loading') }}
            </div>
            <div
                v-else-if="
                    !isStopGeneratorConfirmation &&
                        runningByConditionCode !== null &&
                        runningByConditionCode === 0
                "
                class="dci__status"
            >
                {{
                    $t('dashboard.dci.stopped', {
                        i18nFallback: 'Stopped',
                    })
                }}
            </div>
            <div
                v-else-if="
                    !isStopGeneratorConfirmation &&
                        runningByConditionCode !== null &&
                        runningByConditionCode === 3
                "
                class="dci__status"
            >
                {{
                    $t('dashboard.dci.communication_loss', {
                        i18nFallback: 'Communication loss',
                    })
                }}
            </div>
        </div>

        <!-- Change warning startTimer -->
        <div
            v-if="startTimer.nextChange !== null"
            :style="{ height: bodyHeight + 'px' }"
            class="device-controls__body device-controls__body--generator"
        >
            <change-warning
                @cancel="startTimer.clearChangeTimer()"
                @confirm="startTimer.confirmChange()"
            >
                <div
                    v-html="
                        $t('dashboard.dci.countdown_timer_startstop', {
                            nextState: $t('common.start'),
                            seconds: timerLimit - startTimer.changeSeconds,
                        })
                    "
                ></div>
            </change-warning>
        </div>

        <!-- Change warning autoTimer -->
        <div
            v-else-if="autoTimer.nextChange !== null"
            :style="{ height: bodyHeight + 'px' }"
            class="device-controls__body device-controls__body--generator"
        >
            <change-warning
                @cancel="autoTimer.clearChangeTimer()"
                @confirm="autoTimer.confirmChange()"
            >
                <div
                    v-html="
                        $t('dashboard.dci.countdown_timer_generator', {
                            nextState: autoStartEnabled
                                ? $t('common.disabled')
                                : $t('common.enabled'),
                            seconds: timerLimit - autoTimer.changeSeconds,
                        })
                    "
                ></div>
            </change-warning>
        </div>

        <!-- Reset service warning -->
        <div
            v-else-if="isResetServiceConfirmation"
            :style="{ height: bodyHeight + 'px' }"
            class="device-controls__body device-controls__body--generator"
        >
            <div class="dci__warning">
                <div class="dci__warning__body">
                    <span class="sg-vrm-icon_012_alarm-32px dci__warning__icon"></span>
                    <div
                        v-html="
                            $t('dashboard.reset_service_warning', {
                                device: $t('dashboard.generator').toLowerCase(),
                                i18nFallback: 'Only reset if the generator was serviced.',
                            })
                        "
                    ></div>
                </div>
                <button class="sg-btn sg-element sg-grey" @click="cancelConfirmation()">
                    {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                </button>
                <button class="sg-btn sg-element sg-blue" @click="resetService()">
                    {{ $t('common.confirm', { i18nFallback: 'Confirm' }) }}
                </button>
            </div>
        </div>

        <!-- Stop generator warning -->
        <div
            v-else-if="isStopGeneratorConfirmation"
            :style="{ height: bodyHeight + 'px' }"
            class="device-controls__body device-controls__body--generator"
        >
            <div class="dci__warning">
                <div class="dci__warning__body">
                    <span
                        class="sg-vrm-icon_012_alarm-32px dci__warning__icon dci__warning__icon--red"
                    ></span>
                    <div
                        v-html="
                            $t('dashboard.stopping_warning', {
                                device: $t('dashboard.generator').toLowerCase(),
                                i18nFallback:
                                    'Stopping the generator could cause loss of power. This action cannot be reversed. <br /><br /> Are you sure you want to continue?',
                            })
                        "
                    ></div>
                </div>
                <button class="sg-btn sg-element sg-grey" @click="cancelConfirmation()">
                    {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                </button>
                <button
                    class="sg-btn sg-element sg-red"
                    @click="stopGeneratorTimerWithConfirmation"
                >
                    {{ $t('dashboard.generator_stop', { i18nFallback: 'Stop generator' }) }}
                </button>
            </div>
        </div>

        <!-- Generator body -->
        <div
            v-else
            ref="generatorBody"
            class="device-controls__body device-controls__body--generator"
        >
            <div class="device-controls__row">
                <span
                    class="dci__action dci__action--bold"
                    :class="{ 'dci__action--disabled': isWaitingForResponse }"
                >
                    {{ $t(`dashboard.dci.generator.${runningByConditionCode}`) }}
                </span>
                <clock
                    class="dci__clock "
                    :class="{ 'dci__clock--disabled': isWaitingForResponse }"
                    :seconds="runtime"
                />
            </div>
            <div
                v-if="autoStartEnabled !== null"
                class="device-controls__row device-controls__row--switches"
                :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
            >
                {{ $t(`dashboard.dci.autostart`) }}
                <vrm-controlled-toggle
                    :value="autoStartEnabled"
                    :disabled="isWaitingForResponse"
                    @input="setAutoStartWithLocalTimer"
                />
                <span v-if="autoStartEnabledChange.error" class="dci__warning dci__warning--inline">
                    {{ $t('dashboard.dci.errors.' + autoStartEnabledChange.error) }}
                </span>
            </div>

            <div class="dci__border" />

            <div class="device-controls__row">
                <h4
                    class="dci__subtitle"
                    :class="{ 'dci__subtitle--disabled': isWaitingForResponse }"
                >
                    {{
                        $t('dashboard.dci.manual_control', {
                            i18nFallback: 'Manual Control:',
                        })
                    }}
                </h4>
                <span v-if="manualStartTimerChange.error" class="dci__warning dci__warning--inline">
                    {{ $t('dashboard.dci.errors.' + manualStartTimerChange.error) }}
                </span>
                <span v-else-if="manualStartChange.error" class="dci__warning dci__warning--inline">
                    {{ $t('dashboard.dci.errors.' + manualStartChange.error) }}
                </span>
            </div>
            <div
                class="device-controls__row device-controls__row--switches"
                :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
            >
                {{
                    $t('dashboard.dci.timed_run', {
                        i18nFallback: 'Timed run',
                    })
                }}
                <vrm-toggle
                    v-model="timedRun"
                    :class="{
                        'sg-disabled': isWaitingForResponse || manualStart === 1,
                    }"
                    :disabled="isWaitingForResponse || manualStart === 1"
                />
            </div>
            <div
                class="device-controls__row device-controls__row--switches"
                :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
            >
                <span
                    v-html="
                        $t('dashboard.dci.duration', {
                            i18nFallback: 'Duration <wbr>(hh:mm)',
                        })
                    "
                ></span>
                <div class="dci__time">
                    <input
                        v-if="!manualTimer"
                        :disabled="isWaitingForResponse || !timedRun || manualStart === 1"
                        type="number"
                        min="0"
                        step="1"
                        :value="hours > 0 ? hours : '00'"
                        @change="({ target: { value } }) => setTime({ hours: value })"
                    />
                    <div v-else class="dci__time__time">
                        {{ hours }}
                    </div>
                    <span class="dci__time-separator">:</span>
                    <input
                        v-if="!manualTimer"
                        :disabled="isWaitingForResponse || !timedRun || manualStart === 1"
                        type="number"
                        min="0"
                        step="1"
                        :value="minutes > 0 ? minutes : '00'"
                        @change="
                            ({ target: { value } }) => {
                                setTime({ minutes: value });
                            }
                        "
                    />
                    <div v-else class="dci__time__time">
                        {{ minutes }}
                    </div>
                </div>
            </div>
            <button
                v-if="manualStart !== 1"
                :disabled="isWaitingForResponse || (timedRun && hours === 0 && minutes === 0)"
                class="sg-btn sg-blue sg-element"
                @click="startGeneratorWithLocalTimer"
            >
                {{ $t('dashboard.generator_start', { i18nFallback: 'Start generator' }) }}
            </button>
            <button
                v-else-if="manualStart === 1"
                :disabled="isWaitingForResponse"
                class="sg-btn sg-red sg-element"
                @click="stopGeneratorTimerWithConfirmation"
            >
                {{ $t('dashboard.generator_stop', { i18nFallback: 'Stop generator' }) }}
            </button>

            <div class="dci__border" />

            <div v-if="hasServiceInterval">
                <!-- Generator Service block -->
                <div class="device-controls__row">
                    <h4
                        class="dci__subtitle no-bottom-margin"
                        :class="{ 'dci__subtitle--disabled': isWaitingForResponse }"
                    >
                        {{
                            $t('dashboard.dci.service', {
                                i18nFallback: 'Service:',
                            })
                        }}
                    </h4>

                    <button
                        class="dci-btn__inline"
                        :disabled="isWaitingForResponse"
                        @click="onServiceResetClicked"
                    >
                        {{
                            $t('dashboard.dci.service_button_reset', {
                                i18nFallback: 'Reset',
                            })
                        }}
                    </button>
                </div>
                <div
                    class="device-controls__row device-controls__row--switches"
                    :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
                >
                    <div class="device-controls__row-label">
                        {{
                            $t('dashboard.dci.service_interval', {
                                i18nFallback: 'Service interval',
                            })
                        }}

                        <i
                            v-tooltip.bottom="
                                '<span>' +
                                    $t('dashboard.dci.service_interval_hint', {
                                        i18nFallback: 'This value can be changed on the GX device.',
                                    }) +
                                    '</span>'
                            "
                            data-tooltip-class="device-controls__tooltip"
                            class="sg-vrm-icon_061_info-round-16px"
                        />
                    </div>
                    <span>
                        {{ serviceIntervalInHours }}
                        {{
                            $t('dashboard.dci.label_hours', {
                                i18nFallback: 'hr',
                            })
                        }}
                    </span>
                </div>

                <div
                    class="device-controls__row device-controls__row--switches"
                    :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
                >
                    {{
                        $t('dashboard.dci.runtime_until_service', {
                            i18nFallback: 'Runtime until next service',
                        })
                    }}
                    <span>
                        {{ hoursUntilNextService }}
                        {{
                            $t('dashboard.dci.label_hours', {
                                i18nFallback: 'hr',
                            })
                        }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Clock from './components/Clock.vue';
import VrmToggle from '@/vue/components/shared/toggles/VrmToggle';
import VrmControlledToggle from '@/vue/components/shared/toggles/VrmControlledToggle';
import { createTimer } from './components/Timer';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import Spinner from './components/Spinner';
import ChangeWarning from '@/vue/components/shared/device-controls/controls/components/ChangeWarning';

import { ngInject } from '@/vue/decorators/angular-interop';
import {
    getGeneratorsSubscriptions,
    subscribeToInfo,
    unsubscribeFromInfo,
    reloadIosGeneratorWidgets,
    publishToPath,
} from './utils';
import { newMqttWriteValue, initiateValueLoading, resolveValue } from './writeValueUtils';

@Component({
    components: {
        Clock,
        VrmToggle,
        VrmControlledToggle,
        Spinner,
        ChangeWarning,
    },
})
@AngularLocaleBundle('dashboard')
@AngularLocaleBundle('common')
export default class Generator extends Vue {
    @ngInject()
    MQTTRealtimeService;
    @ngInject()
    $location;
    @ngInject()
    ApiService;

    @Prop({ type: Number, required: true })
    instance;
    @Prop({ type: Boolean, default: false })
    showInstance;
    @Prop({ type: Number, required: true })
    installationId;
    @Prop({ type: String, required: true })
    identifier;
    @Prop({ type: Number, required: true })
    timerLimit;

    timedRun = false;
    hours = 0;
    minutes = 0;

    // controlled by mqtt
    state = null;
    runtime = null;
    manualStart = null;
    manualTimer = null;
    runningByConditionCode = null;
    autoStartEnabled = null;
    serviceCounter = null;
    serviceInterval = null;

    // inherited from createTimer at mount
    startTimer = {};
    autoTimer = {};

    autoStartEnabledChange = newMqttWriteValue({ varName: 'autoStartEnabled' });
    manualStartChange = newMqttWriteValue({ varName: 'manualStart' });
    manualStartTimerChange = newMqttWriteValue({ varName: 'manualTimer' });

    serviceCounterChange = newMqttWriteValue({ varName: 'serviceCounter' });
    serviceIntervalChange = newMqttWriteValue({ varName: 'serviceInterval' });

    waitingForRuntime = false;
    waitingForStop = false;
    waitingForServiceCounter = false;

    /**
     * Keep track of what generator step currently requires confirmation.
     *
     * Possible values:
     * < stop / reset_service >
     *
     * @type {String}
     */
    confirmationStep = null;

    generatorConfirmationSteps = {
        stop: 'stop',
        resetService: 'reset_service',
    };

    bodyHeight = 300;

    mounted() {
        createTimer.call(this, 'startTimer', this.timerLimit);
        createTimer.call(this, 'autoTimer', this.timerLimit);
        this.SUBSCRIPTIONS = getGeneratorsSubscriptions();
        this.subscribeToInfo = subscribeToInfo.bind(this);
        this.unsubscribeFromInfo = unsubscribeFromInfo.bind(this);
        this.publishToPath = publishToPath.bind(this);
        this.initiateValueLoading = initiateValueLoading.bind(this);
        this.resolveValue = resolveValue.bind(this);
        this.subscribeToInfo();
    }

    beforeDestroy() {
        window.clearTimeout(this.autoStartEnabledChange.timer);
        window.clearTimeout(this.manualStartTimerChange.timer);
        window.clearTimeout(this.manualStartChange.timer);
        this.unsubscribeFromInfo();
        this.startTimer.clearChangeTimer();
        this.autoTimer.clearChangeTimer();
    }

    @Watch('serviceCounter')
    onServiceCounterChanged() {
        this.waitingForServiceCounter = false;
    }

    @Watch('autoStartEnabled')
    onAutoStartEnabled() {
        this.resolveValue(this.autoStartEnabledChange, +this.autoStartEnabled);
    }

    @Watch('runtime')
    onRuntime() {
        if (this.waitingForRuntime) {
            this.waitingForRuntime = false;
        }
    }

    @Watch('runningByConditionCode')
    onRunningByConditionCode() {
        if (this.waitingForStop && this.runningByConditionCode === 0) {
            this.waitingForStop = false;
        }
    }

    @Watch('manualStart')
    onManualStartChanged() {
        reloadIosGeneratorWidgets();

        this.resolveValue(this.manualStartChange, this.manualStart);
    }

    @Watch('manualTimer')
    setTimedRun() {
        if (this.manualTimer) {
            this.timedRun = true;
            let seconds = parseInt(this.manualTimer, 10);
            const hours = String(Math.floor(seconds / 3600));
            seconds %= 3600;
            const minutes = String(Math.floor(seconds / 60));

            this.hours = hours.padStart(2, '0');
            this.minutes = minutes.padStart(2, '0');
        }

        this.resolveValue(this.manualStartTimerChange, this.manualTimer);
    }

    get isWaitingForResponse() {
        return (
            this.autoStartEnabledChange.busy ||
            this.manualStartTimerChange.busy ||
            this.manualStartChange.busy ||
            this.waitingForRuntime ||
            this.waitingForStop ||
            this.serviceCounterChange.busy ||
            this.serviceIntervalChange.busy ||
            this.waitingForServiceCounter
        );
    }

    setTime(time) {
        if (time.hours !== undefined) {
            this.hours = parseInt(time.hours, 10);
        }
        if (time.minutes !== undefined) {
            this.minutes = parseInt(time.minutes, 10);
        }
    }

    startGeneratorWithLocalTimer() {
        if (this.isWaitingForResponse) return;
        this.matchBodyHeight();
        this.startTimer.callFuncWithTimer(true, this.startGeneratorTimer);
    }

    startGeneratorTimer() {
        if (this.timedRun) {
            this.manualStartTimerChange.oldState = this.manualTimer;
            this.manualStartTimerChange.newState = this.timeToRun;
            this.initiateValueLoading(this.manualStartTimerChange);

            this.publishToPath(
                {
                    dbusPath: '/ManualStartTimer',
                    callbackIdentifier: `setManualStartTimer${this.instance}`,
                },
                { value: this.timeToRun },
            );
        }
        this.manualStartChange.oldState = this.manualStart;
        this.manualStartChange.newState = 1;
        this.initiateValueLoading(this.manualStartChange);
        this.publishToPath(
            {
                dbusPath: '/ManualStart',
                callbackIdentifier: `setManualStartTimer${this.instance}`,
            },
            { value: 1 },
        );
        this.waitingForRuntime = true;
    }

    stopGeneratorTimerWithConfirmation() {
        if (this.confirmationStep === this.generatorConfirmationSteps.stop) {
            this.confirmationStep = null;
            this.stopGeneratorTimer();
        } else {
            this.matchBodyHeight();
            this.confirmationStep = this.generatorConfirmationSteps.stop;
        }
    }

    stopGeneratorTimer() {
        if (this.isWaitingForResponse) return;

        if (this.manualTimer !== null && this.manualTimer !== 0) {
            this.manualStartTimerChange.oldState = this.manualTimer;
            this.manualStartTimerChange.newState = 0;
            this.initiateValueLoading(this.manualStartTimerChange);
            this.publishToPath(
                {
                    dbusPath: '/ManualStartTimer',
                    callbackIdentifier: `setManualStartTimer${this.instance}`,
                },
                { value: 0 },
            );
        }

        this.manualStartChange.oldState = this.manualTimer;
        this.manualStartChange.newState = 0;
        this.initiateValueLoading(this.manualStartChange);

        this.publishToPath(
            {
                dbusPath: '/ManualStart',
                callbackIdentifier: `setManualStartTimer${this.instance}`,
            },
            { value: 0 },
        );

        this.waitingForStop = true;
    }

    setAutoStartWithLocalTimer(value) {
        if (this.isWaitingForResponse) return;
        this.matchBodyHeight();
        this.autoTimer.callFuncWithTimer(value, this.setAutoStart);
    }

    setAutoStart(value) {
        this.autoStartEnabledChange.oldState = this.autoStartEnabled;
        this.autoStartEnabledChange.newState = +value;
        this.initiateValueLoading(this.autoStartEnabledChange);

        this.publishToPath(
            {
                dbusPath: '/AutoStartEnabled',
                callbackIdentifier: `setAutoStartGenerator${this.instance}`,
            },
            { value: +value },
            this.autoStartEnabled,
        );
    }

    @Watch('timedRun')
    clearTime() {
        if (!this.timedRun) {
            this.hours = 0;
            this.minutes = 0;
        }
    }

    getCallbackIdentifier(varName) {
        return `get_${varName}ofGenerator_${this.instance}`;
    }

    get timeToRun() {
        return this.hours * 60 * 60 + this.minutes * 60;
    }

    get mqttPathObj() {
        return {
            identifier: this.identifier,
            dbusServiceType: 'generator',
            instance: this.instance,
        };
    }

    get locationSearch() {
        return this.$location.search();
    }

    // locationSearch is not reactive so these getters will return the first observed value
    get generatorAction() {
        return this.locationSearch.generatorAction;
    }

    get generatorInstance() {
        return this.locationSearch.generatorInstance;
    }

    get bundleForQueryCommands() {
        return {
            location: this.$location,
            manualStart: this.manualStart,
        };
    }

    @Watch('bundleForQueryCommands', { immediate: true })
    onQueryChange() {
        if (this.manualStart === null) {
            return;
        }
        this.$nextTick(() => {
            const generatorAction = this.$location.search().generatorAction;
            const generatorInstance = this.$location.search().generatorInstance;

            if (generatorAction === undefined || Number(generatorInstance) !== this.instance) {
                return;
            }
            if (generatorAction === 'toggle_generator') {
                this.manualStart === 1
                    ? this.stopGeneratorTimerWithConfirmation()
                    : this.startGeneratorWithLocalTimer();
            }

            this.$location.search('generatorAction', null);
            this.$location.search('generatorInstance', null);
            this.$location.search('mobileOpenControls', null);
        });
    }

    matchBodyHeight() {
        if (this.$refs.generatorBody !== undefined)
            this.bodyHeight = this.$refs.generatorBody.clientHeight;
    }

    onServiceResetClicked() {
        this.confirmationStep = this.generatorConfirmationSteps.resetService;
    }

    /**
     * Check if the user currently needs to confirm stopping the generator.
     *
     * @returns {boolean}
     */
    get isStopGeneratorConfirmation() {
        return this.confirmationStep === this.generatorConfirmationSteps.stop;
    }

    /**
     * Check if the user currently needs to confirm resetting the service timer.
     *
     * @returns {boolean}
     */
    get isResetServiceConfirmation() {
        return this.confirmationStep === this.generatorConfirmationSteps.resetService;
    }

    /**
     * Get the hours until next service.
     *
     * @returns {number}
     */
    get hoursUntilNextService() {
        return this.serviceCounter ? Math.round(this.serviceCounter / 3600) : 0;
    }

    /**
     * Get the configured service interval in hours.
     *
     * @returns {number}
     */
    get serviceIntervalInHours() {
        return this.serviceInterval >= 0 ? Math.round(this.serviceInterval / 3600) : 0;
    }

    /**
     * Checks if a service interval is configured.
     *
     * @returns {boolean}
     */
    get hasServiceInterval() {
        return this.serviceIntervalInHours > 0;
    }

    /**
     * Send the ServiceCounterReset event via MQTT
     */
    resetService() {
        this.publishToPath(
            {
                dbusPath: '/ServiceCounterReset',
                callbackIdentifier: `setServiceCounterReset${this.instance}`,
            },
            { value: 1 },
            0,
        );

        if (this.serviceIntervalInHours > this.hoursUntilNextService) {
            this.waitingForServiceCounter = true;
        }

        this.confirmationStep = null;
    }

    /**
     * Cancel any open confirmation window.
     */
    cancelConfirmation() {
        this.confirmationStep = null;
    }
}
</script>
